import { useState, useMemo } from "react";
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Button, SearchBar, GeneralMessage } from "../../../../common/APICommon";
import { LoadingSpinner } from "../../../../common/Loading";
import AddIcon from "@mui/icons-material/Add";
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { ApproverMaintenanceTable } from "./components/ApproverMaintenanceTable";
import { ApproverMaintenanceModal } from "./components/ApproverMaintenanceModal";
import { useApproverMaintenance } from "../../../hooks/useApproverMaintenance";

export function ApproverMaintenanceModule() {
  const {
    loading,
    groupedData,
    approverMaintenanceTypes,
    saveApproverMaintenance,
    searchTerm,
    setSearchTerm,
    showMessage, // From Hook
    message,     // From Hook
    isSuccess,   // From Hook
    showGeneralMessage // From Hook
  } = useApproverMaintenance();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const [currentPage, setCurrentPage] = useState(1);

  const typeIds = useMemo(() => Object.keys(groupedData), [groupedData]);

  const handleTabSelect = (index) => {
    setSelectedTabIndex(index);
    setCurrentPage(1);
    setSearchTerm("");
  };

  const handleOpenModal = (item = null, mode = "edit") => {
    setSelectedItem(item);
    setModalMode(item ? mode : "create");
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null); // CRITICAL: Clear the selected item here
  };

  const tabStyle = (index) => `px-4 py-2 cursor-pointer font-medium transition-colors outline-none select-none ${selectedTabIndex === index
    ? "bg-[#668fca] text-white rounded-t-lg"
    : "text-[#668fca] hover:text-[#4a6ea5]"
    }`;

  return (
    <>
      <div className='p-4 space-y-4'>
        <div className="flex justify-between items-center mb-6">
          <SearchBar value={searchTerm} handleFilter={setSearchTerm} />
          <Button
            icon={<AddIcon />}
            name={"Create Config"}
            className={"bg-customGreen hover:bg-customGreenHover border-none"}
            onClick={() => handleOpenModal(null, "create")}
          />
        </div>

        {typeIds.length > 0 ? (
          <Tabs selectedIndex={selectedTabIndex} onSelect={handleTabSelect}>
            <TabList className="flex flex-row items-center border-b border-gray-200 mb-4 list-none p-0">
              {typeIds.map((typeId, index) => {
                const label = approverMaintenanceTypes.find(t => t.value.toString() === typeId)?.name || `Type ${typeId}`;
                return <Tab key={typeId} className={tabStyle(index)}>{label}</Tab>;
              })}
            </TabList>

            {typeIds.map((typeId) => (
              <TabPanel key={typeId}>
                <ApproverMaintenanceTable
                  data={groupedData[typeId] || []}
                  onEdit={(item) => handleOpenModal(item, "edit")}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </TabPanel>
            ))}
          </Tabs>
        ) : (
          !loading && (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-gray-100">
              <SearchOffIcon sx={{ fontSize: 48, color: '#668fca' }} />
              <p className="mt-2 text-gray-500 font-medium">No records found</p>
            </div>
          )
        )}
      </div>

      <ApproverMaintenanceModal
        isOpen={showModal}
        onClose={handleCloseModal}
        selectedData={selectedItem}
        mode={modalMode}
        onSave={saveApproverMaintenance}
        approverMaintenanceTypes={approverMaintenanceTypes}
        loading={loading}
        showGeneralMessage={showGeneralMessage} // Pass hook's trigger to modal
      />

      {/* Render feedback based on hook state */}
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loading} />
    </>
  );
}